/**
 * Chart Display Integration Component for Chat
 * 
 * Integrates chart functionality into the chat system by providing
 * a unified interface for rendering different chart types based on
 * AI-generated configurations using proper shadcn/ui chart primitives.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltipContent, ChartLegendContent } from "@/components/ui/chart";
import React from "react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from "recharts";

/**
 * Chart configuration interface for AI-generated chart data
 */
export interface ChartConfig {
  /**
   * Type of chart to render
   */
  type: "bar" | "line" | "pie" | "area";

  /**
   * Chart title
   */
  title: string;

  /**
   * Chart data as array of objects
   */
  data: Array<Record<string, any>>;

  /**
   * Chart styling configuration mapping keys to colors
   */
  chartConfig: Record<string, any>;
}

/**
 * Props interface for ChartDisplay component
 */
export interface ChartDisplayProps {
  /**
   * Chart configuration from AI tool response
   */
  config: ChartConfig;

  /**
   * Optional CSS className for styling
   */
  className?: string;
}

/**
 * ChartDisplay Component
 * 
 * Renders charts using proper shadcn/ui chart primitives and direct recharts
 * composition. Each chart type is implemented using ChartContainer with proper
 * color management through CSS variables.
 */
export function ChartDisplay({ config, className }: ChartDisplayProps) {
  // Validate configuration
  if (!config || !config.type || !config.data || !config.title) {
    return (
      <div className={`rounded-lg border border-destructive/20 bg-destructive/10 p-4 ${className || ""}`}>
        <div className="text-sm text-destructive">
          Invalid chart configuration. Missing required fields.
        </div>
      </div>
    );
  }

  // Early return if no data provided
  if (!config.data || config.data.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>{config.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get data structure information
  const firstRow = config.data[0] || {};
  const dataKeys = Object.keys(firstRow);

  // Build chartConfig from data colors when chartConfig is empty
  const chartConfig = React.useMemo(() => {
    if (config.chartConfig && Object.keys(config.chartConfig).length > 0) {
      return config.chartConfig;
    }

    // Generate chartConfig from data colors since server sends colors in data array
    return config.data.reduce((acc, item, index) => {
      const key = item.name.toLowerCase().replace(/\s+/g, '-');
      acc[key] = {
        label: item.name,
        color: item.color || `hsl(var(--chart-${(index % 5) + 1}))`
      };
      return acc;
    }, {} as Record<string, any>);
  }, [config.data, config.chartConfig]);

  // Auto-detect axes for cartesian charts
  const nameKey = dataKeys.find(key => typeof firstRow[key] === 'string') || dataKeys[0];
  const valueKeys = dataKeys.filter(key => key !== nameKey && typeof firstRow[key] === 'number');

  // Render chart based on type using proper shadcn/ui structure
  switch (config.type) {
    case "bar":
      return (
        <Card className={className}>
          <CardHeader>
            <CardTitle>{config.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={config.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={nameKey} />
                  <YAxis />
                  <Tooltip content={<ChartTooltipContent />} />
                  {/* <Legend content={<ChartLegendContent />} verticalAlign="bottom" align="center" /> */}
                  {valueKeys.map((key) => {
                    const colorKey = key.toLowerCase().replace(/\s+/g, '-');
                    return (
                      <Bar
                        key={colorKey}
                        dataKey={key}
                        fill={`var(--color-${colorKey})`}
                        name={key}
                        radius={4}
                      />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      );

    case "line":
      return (
        <Card className={className}>
          <CardHeader>
            <CardTitle>{config.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={config.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={nameKey} />
                  <YAxis />
                  <Tooltip content={<ChartTooltipContent />} />
                  {/* <Legend content={<ChartLegendContent />} verticalAlign="bottom" align="center" /> */}
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={Object.values(chartConfig)[0]?.color || "#8884d8"}
                    strokeWidth={2}
                    dot={{ fill: Object.values(chartConfig)[0]?.color || "#8884d8", strokeWidth: 2 }}
                    name="Value"
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      );

    case "area":
      return (
        <Card className={className}>
          <CardHeader>
            <CardTitle>{config.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={config.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={nameKey} />
                  <YAxis />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Legend content={<ChartLegendContent />} verticalAlign="bottom" align="center" />
                  {config.data.map((item) => {
                    const colorKey = item.name.toLowerCase().replace(/\s+/g, '-');
                    return (
                      <Area
                        key={colorKey}
                        type="monotone"
                        dataKey="value"
                        stroke={`var(--color-${colorKey})`}
                        fill={`var(--color-${colorKey})`}
                        name={item.name}
                      />
                    );
                  })}
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      );

    case "pie":
      return (
        <Card className={className}>
          <CardHeader>
            <CardTitle>{config.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip content={<ChartTooltipContent />} />
                  <Pie
                    data={config.data}
                    dataKey={valueKeys[0] || dataKeys[1]}
                    nameKey={nameKey}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                  >
                    {config.data.map((entry, index) => {
                      // Create consistent color key from the name field
                      const nameValue = entry[nameKey];
                      const colorKey = nameValue?.toString().toLowerCase().replace(/\s+/g, '-') || `item-${index}`;

                      // Try to get color from chart config, fallback to default chart colors
                      const configColor = chartConfig[nameValue] || chartConfig[colorKey];
                      const fallbackColor = `hsl(var(--chart-${(index % 5) + 1}))`;
                      const cellColor = configColor?.color || fallbackColor;

                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={cellColor}
                        />
                      );
                    })}
                  </Pie>
                  <Legend content={<ChartLegendContent />} verticalAlign="bottom" align="center" />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      );

    default:
      return (
        <div className={`rounded-lg border border-destructive/20 bg-destructive/10 p-4 ${className || ""}`}>
          <div className="text-sm text-destructive">
            Unsupported chart type: {config.type}. Supported types are: bar, line, pie, area.
          </div>
        </div>
      );
  }
}